import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Applies a promotional discount code from the promo button.
   * @param {MouseEvent} event - The click event on the promo button.
   */
  applyPromoDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const trigger = event.currentTarget instanceof HTMLElement ? event.currentTarget : event.target;
    if (!(trigger instanceof HTMLElement)) return;

    const promoCode = trigger.dataset.discountCode;
    if (!promoCode) return;

    const discountInput = this.querySelector('input[name="discount"]');
    const form = this.querySelector('.cart-discount__form');
    if (!(discountInput instanceof HTMLInputElement) || !(form instanceof HTMLFormElement)) return;

    discountInput.value = promoCode;

    // requestSubmit dispatches a real submit event with form as target,
    // which the on:submit binding picks up via applyDiscount.
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new SubmitEvent('submit', { bubbles: true, cancelable: true }));
    }
  };

  /**
   * Submits a new discount code from the cart form.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountInput = form.querySelector('input[name="discount"]');
    if (!(discountInput instanceof HTMLInputElement)) return;

    const discountCodeValue = discountInput.value.trim();
    if (!discountCodeValue) return;

    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return;

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`Cart update failed (${response.status})`);

      const data = await response.json();
      const apiDiscountCodes = Array.isArray(data?.discount_codes) ? data.discount_codes : [];

      const isInapplicable = apiDiscountCodes.some(
        (/** @type {{ code: string; applicable: boolean }} */ d) =>
          d.code === discountCodeValue && d.applicable === false
      );
      if (isInapplicable) {
        discountInput.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data?.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string') return;

      // Detect a "no-op shipping discount": Shopify reports the code as applicable
      // but the rendered section did not actually add a new pill (e.g. shipping
      // discounts that only show at checkout). Surface a clearer message.
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      if (section) {
        const renderedCodes = Array.from(section.querySelectorAll('.cart-discount__pill'))
          .map((el) => (el instanceof HTMLLIElement ? el.dataset.discountCode : null))
          .filter(/** @returns {c is string} */ (c) => typeof c === 'string');

        const wasApplicable = apiDiscountCodes.some(
          (/** @type {{ code: string; applicable: boolean }} */ d) =>
            d.code === discountCodeValue && d.applicable === true
        );
        const sameAsBefore =
          renderedCodes.length === existingDiscounts.length &&
          renderedCodes.every((code) => existingDiscounts.includes(code));

        if (sameAsBefore && wasApplicable) {
          discountInput.value = '';
          this.#handleDiscountError('shipping');
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      if (this.#isAbortError(error)) return;
      console.error('[cart-discount] applyDiscount failed:', error);
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event instanceof KeyboardEvent && event.key !== 'Enter') return;
    if (typeof this.dataset.sectionId !== 'string') return;

    // Element (not HTMLElement) so clicks on the inner SVG resolve correctly.
    const target = event.target;
    if (!(target instanceof Element)) return;

    const pill = target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const remainingDiscounts = this.#existingDiscounts().filter((c) => c !== discountCode);
    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: remainingDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`Cart update failed (${response.status})`);

      const data = await response.json();
      const newHtml = data?.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string') return;

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      if (this.#isAbortError(error)) return;
      console.error('[cart-discount] removeDiscount failed:', error);
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Reveals the relevant error message under the form.
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Reads currently-rendered discount codes from the pill list.
   * @returns {string[]}
   */
  #existingDiscounts() {
    return Array.from(this.querySelectorAll('.cart-discount__pill'))
      .map((pill) => (pill instanceof HTMLLIElement ? pill.dataset.discountCode : null))
      .filter(/** @returns {c is string} */ (c) => typeof c === 'string');
  }

  /**
   * @param {unknown} error
   * @returns {boolean}
   */
  #isAbortError(error) {
    return error instanceof DOMException && error.name === 'AbortError';
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
